import { useModal } from '@/contexts/ModalContext'
import { MODAL_TYPES } from '@/libs/config/modal'
import Cookie from 'js-cookie'
import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectSections } from 'src/redux/customisation'
import { selectDetailMenuSite } from 'src/redux/meta'
import ExitIntentTrigger from './ExitIntentTrigger'
import PreviewModeUISync from './PreviewModeUISync'
import Timer from './Timer'
import PageScrollTracker from './PageScrollTracker'

const popupFrequencyInDays = {
  once: Infinity,
  daily: 1,
  weekly: 7,
  monthly: 30,
}

interface MarketingPopupProps {
  id: string
  previewMode: boolean
}

const MarketingPopup = ({
  id,
  previewMode,
}: MarketingPopupProps): ReactNode => {
  const { settings, updatedAt } = useSelector(selectSections)[
    id
  ] as Customisation.MarketingPopupSection
  const { menu } = useSelector(selectDetailMenuSite) || {}
  const [triggerEnabled, setTriggerEnable] = useState(true)
  const { openModal, activeModal } = useModal()

  const getLastShownCookieExpiry = () => {
    return popupFrequencyInDays[settings.popupFrequency]
  }

  const resetCookieOnSettingsUpdate = useCallback(() => {
    if (
      Cookie.get('marketingPopupLastUpdate') &&
      parseInt(updatedAt) !==
        parseInt(Cookie.get('marketingPopupLastUpdate') as string, 10)
    ) {
      Cookie.set('marketingPopupLastUpdate', updatedAt, { expires: Infinity })
      Cookie.remove('marketingPopupLastShown')
    }
  }, [updatedAt])

  const toggleMarketingPopupTrigger = ({
    triggerEnabled,
  }: {
    triggerEnabled: boolean
  }) => setTriggerEnable(triggerEnabled)

  const showMarketingPopupModal = (): void => {
    if (!settings.enableMarketingPopup) return
    if (
      previewMode &&
      (activeModal === MODAL_TYPES.NEWSLETTER_SIGNUP || !triggerEnabled)
    ) {
      return
    }
    if (!Cookie.get('marketingPopupLastShown')) {
      openModal(MODAL_TYPES.MARKETING_POPUP, {
        settings,
      })
      Cookie.set('marketingPopupLastShown', new Date().getTime().toString(), {
        expires: getLastShownCookieExpiry(),
      })
    }
  }
  const { triggerType, timeDelayTrigger, pageScrollTrigger } = settings

  useEffect(() => {
    resetCookieOnSettingsUpdate()
  }, [])

  useEffect(() => {
    if (previewMode) {
      resetCookieOnSettingsUpdate()
    }
  }, [previewMode, resetCookieOnSettingsUpdate])

  return (
    <React.Fragment>
      <PreviewModeUISync
        sectionId={id}
        menuId={menu?.id as string}
        previewMode={previewMode}
        toggleMarketingPopupTrigger={toggleMarketingPopupTrigger}
      />
      {triggerType === 'timeDelay' && (
        <Timer
          onTimerExpired={showMarketingPopupModal}
          duration={timeDelayTrigger}
        />
      )}
      {triggerType === 'pageScroll' && (
        <PageScrollTracker
          onScrollThresholdPassed={showMarketingPopupModal}
          percentageThreshold={pageScrollTrigger}
        />
      )}
      {triggerType === 'exitIntent' && (
        <ExitIntentTrigger onExitIntentTrigger={showMarketingPopupModal} />
      )}
    </React.Fragment>
  )
}

export default MarketingPopup
